import { z } from 'zod';
import { ROLES } from './access.ts';
import { MESSAGE_CHANNELS } from './audience.ts';

/**
 * Approval workflow (BB-106, ADM-008): an environment may require that a reviewer approves the exact revision of a
 * campaign (or Canvas) before it is launched; any change to the draft invalidates the approval. Messaging rules add
 * requirements: a large audience, a channel or a tag needs an approver with a given role.
 */
export const MessagingRule = z.discriminatedUnion('kind', [
  z.strictObject({ id: z.string().min(1).max(40), kind: z.literal('audience_size_over'), threshold: z.number().int().min(1), requiresRole: z.enum(ROLES) }),
  z.strictObject({ id: z.string().min(1).max(40), kind: z.literal('channel'), channel: z.enum(MESSAGE_CHANNELS), requiresRole: z.enum(ROLES) }),
  z.strictObject({ id: z.string().min(1).max(40), kind: z.literal('tag'), tagId: z.uuid(), requiresRole: z.enum(ROLES) }),
  /** Transactional campaigns (e.g. trading alerts) launch without approval. */
  z.strictObject({ id: z.string().min(1).max(40), kind: z.literal('transactional_exemption') }),
]);
export type MessagingRule = z.infer<typeof MessagingRule>;

/** `environment.settings.approvals`; unset means: on in production environments, off elsewhere. */
export const ApprovalPolicy = z.strictObject({
  campaigns: z.boolean(),
  canvases: z.boolean(),
  /** Whether the person who asked for approval may approve it themselves. */
  allowSelfApprove: z.boolean(),
  rules: z.array(MessagingRule).max(20),
});
export type ApprovalPolicy = z.infer<typeof ApprovalPolicy>;

export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'invalidated'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const RequestApprovalBody = z.strictObject({ comment: z.string().trim().max(1000).optional() }).default({});
export const DecideApprovalBody = z.strictObject({ comment: z.string().trim().max(1000).optional() }).default({});
export const RejectApprovalBody = z.strictObject({ comment: z.string().trim().min(3).max(1000) });
export const ApprovalListQuery = z.object({ status: z.enum([...APPROVAL_STATUSES, 'all']).default('pending'), objectId: z.uuid().optional() });

export interface TriggeredRule {
  ruleId: string;
  kind: MessagingRule['kind'];
  requiresRole: (typeof ROLES)[number] | null;
  reason: string;
}

export interface ApprovalPerson {
  id: string;
  email: string | null;
}

export interface ApprovalRequestView {
  id: string;
  objectType: 'campaign' | 'canvas';
  objectId: string;
  objectName: string;
  revision: number;
  contentHash: string;
  status: ApprovalStatus;
  requestedBy: ApprovalPerson | null;
  requestedAt: string;
  requestComment: string | null;
  decidedBy: ApprovalPerson | null;
  decidedAt: string | null;
  decisionComment: string | null;
  triggeredRules: TriggeredRule[];
}

/** Where the draft of a campaign stands (shown in the review panel). */
export interface ApprovalState {
  required: boolean;
  /** `missing`: required, but nobody asked yet (or the last request was invalidated by a change). */
  status: 'not_required' | 'missing' | 'pending' | 'approved' | 'rejected';
  request: ApprovalRequestView | null;
}

export interface ApprovalPolicyView {
  policy: ApprovalPolicy;
  /** `true` when the environment never set a policy and the default for its kind applies. */
  isDefault: boolean;
}
